/**
 * The start values a person entered for the last run of a conversation
 * (agion/Agion#2413), so the next run in the same conversation opens its form
 * with them.
 *
 * One record per user and conversation (mission), in the tab's
 * sessionStorage, as failedSendStore keeps a send: it survives a reload of
 * the tab and ends with the tab. Reading forgets every other user's records,
 * and signing out forgets all of them (services/auth.ts).
 *
 * Storage can be off or full (private mode, quota); every call then does
 * nothing and the form opens with the mission's defaults.
 */

const PREFIX = 'agion.chat.launchValues.v1:'

function keyOf(userId: string, missionId: string): string {
  return `${PREFIX}${encodeURIComponent(userId)}:${encodeURIComponent(missionId)}`
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/** The keys this module wrote that `matches` selects. */
function ownKeys(store: Storage, matches: (key: string) => boolean): string[] {
  const keys: string[] = []
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i)
    if (key?.startsWith(PREFIX) && matches(key)) keys.push(key)
  }
  return keys
}

/**
 * The values this user entered for the last run of this conversation, or
 * null. Forgets every other user's records first, so a tab that changes hands
 * keeps none. Only string values are read back.
 */
export function loadLaunchValues(userId: string, missionId: string): Record<string, string> | null {
  const store = storage()
  if (!store) return null
  try {
    const own = `${PREFIX}${encodeURIComponent(userId)}:`
    for (const key of ownKeys(store, (k) => !k.startsWith(own))) store.removeItem(key)
    const raw = store.getItem(keyOf(userId, missionId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const values: Record<string, string> = {}
    for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string') values[name] = value
    }
    return values
  } catch {
    return null
  }
}

export function saveLaunchValues(userId: string, missionId: string, values: Record<string, string>): void {
  try {
    storage()?.setItem(keyOf(userId, missionId), JSON.stringify(values))
  } catch {
    // Full or off: the next run opens with the mission's defaults.
  }
}

/** Forgets every kept value in this tab, for every user: at sign-out. */
export function forgetAllLaunchValues(): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of ownKeys(store, () => true)) store.removeItem(key)
  } catch {
    // Off: nothing was kept.
  }
}
